import React from "react";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SavingsIcon from "@mui/icons-material/Savings";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PolicyIcon from "@mui/icons-material/Policy";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import GavelIcon from "@mui/icons-material/Gavel";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PetsIcon from "@mui/icons-material/Pets";
import LanguageIcon from "@mui/icons-material/Language";
import InfoIcon from "@mui/icons-material/Info";
import WorkIcon from "@mui/icons-material/Work";
import BadgeIcon from "@mui/icons-material/Badge";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CarRentalIcon from "@mui/icons-material/CarRental";
import BuildIcon from "@mui/icons-material/Build";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import SourceIcon from "@mui/icons-material/Source";
import GroupsIcon from "@mui/icons-material/Groups";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RouterIcon from "@mui/icons-material/Router";
import ArticleIcon from "@mui/icons-material/Article";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

/**
 * Get the appropriate icon component for an entity type
 */
const getIconForEntityType = (entityType: string): React.ReactElement => {
  const type = entityType.toLowerCase();

  // People-related entities
  if (
    type.includes("individual") ||
    type.includes("person") ||
    type.includes("claimant")
  ) {
    return <PersonIcon />;
  }

  // Driver
  if (type.includes("driver")) {
    return <DriveEtaIcon />;
  }

  // Occupation
  if (type.includes("occupation")) {
    return <WorkIcon />;
  }

  // Vehicle-related entities (most specific first)
  if (type.includes("vehicleinspection")) {
    return <CarRepairIcon />;
  }

  if (type.includes("vehiclemot") || type.includes("mot")) {
    return <BuildIcon />;
  }

  if (type.includes("vehicle") || type.includes("car")) {
    return <DirectionsCarIcon />;
  }

  // Vehicle Hire/Rental
  if (type.includes("hire")) {
    return <CarRentalIcon />;
  }

  if (type.includes("email")) {
    return <EmailIcon />;
  }

  // Location/Address entities
  if (type.includes("location") || type.includes("address")) {
    return <LocationOnIcon />;
  }

  // Organization/Business entities
  if (
    type.includes("organisation") ||
    type.includes("organization") ||
    type.includes("company") ||
    type.includes("business")
  ) {
    return <BusinessIcon />;
  }

  // Contact entities
  if (type.includes("phone") || type.includes("contact")) {
    return <PhoneIcon />;
  }

  // Financial entities
  if (type.includes("bankaccount") || type.includes("bank")) {
    return <AccountBalanceIcon />;
  }
  if (type.includes("creditcard") || type.includes("credit")) {
    return <CreditCardIcon />;
  }
  if (type.includes("recovery")) {
    return <TrendingUpIcon />;
  }
  if (type.includes("reserve")) {
    return <SavingsIcon />;
  }
  if (type.includes("transaction")) {
    return <ReceiptIcon />;
  }
  if (type.includes("quotation")) {
    return <RequestQuoteIcon />;
  }
  if (type.includes("membership")) {
    return <CardMembershipIcon />;
  }

  // Insurance/Policy
  if (type.includes("insurancepolicy") || type.includes("policy")) {
    return <PolicyIcon />;
  }

  // Claims and incidents
  if (type.includes("claim") || type.includes("incident")) {
    return <AssignmentIcon />;
  }

  // Instructions (Legal)
  if (type.includes("instruction")) {
    return <GavelIcon />;
  }

  // Identification Documents (most specific first)
  if (type.includes("drivinglicense") || type.includes("drivinglicence")) {
    return <BadgeIcon />;
  }

  if (type.includes("passport")) {
    return <CardTravelIcon />;
  }

  if (type.includes("nationalinsurance")) {
    return <FingerprintIcon />;
  }

  // Generic license (after specific checks)
  if (type.includes("license") || type.includes("licence")) {
    return <VerifiedUserIcon />;
  }

  // Legal/Conviction
  if (type.includes("drivingconviction") || type.includes("conviction")) {
    return <GavelIcon />;
  }

  // Medical/Injury
  if (type.includes("injury")) {
    return <LocalHospitalIcon />;
  }

  // Intelligence
  if (type.includes("intelligence")) {
    return <LightbulbIcon />;
  }

  // Intervention
  if (type.includes("intervention")) {
    return <SupportAgentIcon />;
  }

  // Resolution
  if (type.includes("resolution")) {
    return <TaskAltIcon />;
  }

  // Fraud Ring (most specific first)
  if (type.includes("fraudring")) {
    return <GroupsIcon />;
  }

  if (type.includes("fraud")) {
    return <ReportProblemIcon />;
  }

  // Ring (after fraud ring check)
  if (type.includes("ring")) {
    return <GroupsIcon />;
  }

  // Pets
  if (type.includes("pet")) {
    return <PetsIcon />;
  }

  // Website/Internet
  if (type.includes("website")) {
    return <LanguageIcon />;
  }

  // IP Address/Computer
  if (type.includes("ipaddress")) {
    return <RouterIcon />;
  }

  // Source data
  if (type.includes("source")) {
    return <SourceIcon />;
  }

  // Additional Information
  if (type.includes("additionalinformation")) {
    return <InfoIcon />;
  }

  // Generic documents
  if (type.includes("document")) {
    return <ArticleIcon />;
  }

  return <BusinessIcon />;
};

export default getIconForEntityType;
